import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { Router } from '@angular/router';
import { from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService, SessionEndedError } from '../services/auth.service';
import { ToastService } from '../services/toast.service';
import { isTokenExpired } from '../../shared/utils/token-expiry.util';

/** Calls made before there is a session, or that end it: they carry no token and never start a refresh. */
const TOKEN_FREE_PATHS = ['/Account/user-login', '/Account/verify-two-factor', '/Account/refresh-token', '/Account/logout'];

/** A token this close to running out is replaced first, so a request cannot leave with one that dies on the way. */
const EXPIRY_MARGIN_SECONDS = 30;

export const authInterceptor: HttpInterceptorFn = (request, next) => {
    const authService = inject(AuthService);
    const toastService = inject(ToastService);
    const router = inject(Router);

    if (TOKEN_FREE_PATHS.some((path) => request.url.includes(path))) {
        return next(request);
    }

    const token = authService.getAccessToken();
    if (!token) {
        return next(request);
    }

    const sendWith = (accessToken: string) => next(request.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } }));

    // Swaps the refresh token for a new access token and sends the request again with it. If the API refuses the
    // refresh token the session is over; any other failure (e.g. offline) is passed on and leaves the session alone.
    const refreshAndSend = (staleToken: string) =>
        from(authService.refreshAccessToken(staleToken)).pipe(
            catchError((error) => {
                if (error instanceof SessionEndedError) {
                    if (authService.endSession()) {
                        toastService.error('Your session has expired. Please log in again to continue.', 'Session Expired');
                        router.navigate(['/login']);
                    }
                }
                return throwError(() => error);
            }),
            switchMap(sendWith)
        );

    let expired = true; // a token that cannot be decoded is as good as an expired one
    try {
        expired = isTokenExpired(jwtDecode(token), EXPIRY_MARGIN_SECONDS);
    } catch {
        // falls through to a refresh
    }

    if (expired) {
        return refreshAndSend(token);
    }

    // The server can still refuse a token the browser thinks is fine (clock drift, revoked user): refresh once and retry once.
    return sendWith(token).pipe(
        catchError((error) => (error instanceof HttpErrorResponse && error.status === 401 ? refreshAndSend(token) : throwError(() => error)))
    );
};
